import { Destroyer, EMouseButtons } from '@adaptiva/core/common';
import { checkBit } from '@adaptiva/core/math';
import { Vector2 } from '@adaptiva/core/space';
import { Directive, EffectRef, ElementRef, InputSignal, ModelSignal, Signal, WritableSignal, computed, contentChild, effect, forwardRef, inject, input, model, signal } from '@angular/core';
import { IViewbox } from './viewbox.interface';
import { ViewboxService } from './viewbox.service';

@Directive({
	selector: '[adaViewboxDirective]',
	host: {
		'tabindex': '0',
		'[style.outline]': '"none"',
		'(window:mousemove)': 'onMouseMove($event)',
		'(mousedown)': 'onMouseDown($event)',
		'(window:mouseup)': 'onMouseUp($event)',
		'(wheel)': 'onMouseWheel($event)',
		'(keydown.space)': 'onSpaceDown($event)',
		'(keyup.space)': 'onSpaceUp($event)',
	},
	providers: [
		{
			provide: IViewbox,
			useExisting: forwardRef(() => ViewboxDirective)
		}
	],

})
export class ViewboxDirective extends Destroyer implements IViewbox {

	public readonly x: ModelSignal<float> = model(0.0);

	public readonly y: ModelSignal<float> = model(0.0);

	public readonly z: ModelSignal<float> = model(1.0);

	public readonly canPan: ModelSignal<boolean> = model(true);

	public readonly minZoom: InputSignal<float> = input(0.25);

	public readonly zoomFactor: InputSignal<float> = input(0.001);

	protected readonly animationLength: float = 0.3;

	public readonly boundsEffect: EffectRef = effect(() => {
		this.resizeObserver.observe(this.contentWrapper().nativeElement.parentElement);
	});

	protected readonly previousTarget: WritableSignal<string> = signal('');

	protected readonly isNewTarget: Signal<boolean> = computed(() => {
		const _domTarget: string = this.viewboxService.targetSelector();
		const _previousTarget: string = this.previousTarget();

		if (_domTarget && (_domTarget !== _previousTarget || !_previousTarget))
			return true;

		return false;
	});

	protected readonly targetBounds: Signal<DOMRect> = computed(() => {
		const _target: string = this.viewboxService.targetSelector();

		if (_target && !this.waitingForResize() && this.isNewTarget())
			return this.elementRef.nativeElement.querySelector(_target).getBoundingClientRect();
	});

	protected readonly resizeObserver: ResizeObserver = new ResizeObserver((e: ResizeObserverEntry[]) => this._handleResize(e));

	protected readonly waitingForResize: WritableSignal<boolean> = signal(true);

	protected readonly contentWrapper: Signal<ElementRef<HTMLElement>> = contentChild<ElementRef<HTMLElement>>('ContentWrapper');

	protected readonly canvasOffsetPoint: WritableSignal<Vector2> = signal(new Vector2());

	protected readonly mouseX: WritableSignal<float> = signal(0.0);

	protected readonly mouseY: WritableSignal<float> = signal(0.0);

	protected readonly mouseView: Signal<Vector2> = computed(() => {
		const _zoom: float = this.z();
		const _worldX: float = (this.mouseX() - this.x()) / _zoom;
		const _worldY: float = (this.mouseY() - this.y()) / _zoom;

		return new Vector2(_worldX, _worldY);
	});

	protected readonly isPanning: WritableSignal<boolean> = signal(false);

	protected readonly isMouseDown: WritableSignal<boolean> = signal(false);

	protected readonly mouseDownPoint: WritableSignal<Vector2> = signal(new Vector2());

	protected readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);

	protected readonly viewboxService: ViewboxService = inject(ViewboxService);

	protected readonly centerCanvasEffect: EffectRef = effect(() => {
		if (!this.waitingForResize() && this.isNewTarget()) {
			const _bounds: DOMRect = this.contentWrapper().nativeElement.parentElement.getBoundingClientRect();
			const _targetBounds: DOMRect = this.targetBounds();

			if (_bounds && _targetBounds) {
				this.applyTransition();

				const _finalX: float = -_targetBounds.x + _bounds.x + this.getCenter().x - (_targetBounds.width / 2) + (this.x());
				const _finalY: float = -_targetBounds.y + _bounds.y + this.getCenter().y - (_targetBounds.height / 2) + (this.y());

				this.x.set(_finalX);
				this.y.set(_finalY);
				this.z.set(1);

				this._fastUpdate(_finalX, _finalY, 1);
				this.previousTarget.set(this.viewboxService.targetSelector());
			}
		}
	});

	protected readonly canvasStyleEffect: EffectRef = effect(() => {
		const _wrapper: HTMLElement = this.contentWrapper()?.nativeElement;

		if (!_wrapper)
			return;

		let _cursorStyle: string = 'default';
		let _userSelect: string = 'auto';

		if (this.canPan()) {
			_userSelect = 'none';
			_cursorStyle = this.isMouseDown() ? 'grabbing' : 'grab';
		} else if (!this.canPan() && this.isMouseDown())
			_cursorStyle = 'not-allowed';

		_wrapper.style.setProperty('user-select', _userSelect);
		_wrapper.parentElement.style.setProperty('cursor', _cursorStyle);
	});

	private _previousCanPanState: boolean = true;

	public getCenter(): Vector2 {
		const _bounds: DOMRect = this.contentWrapper().nativeElement.parentElement.getBoundingClientRect();
		const _centerX: float = ((_bounds.width / 2)) / this.z();
		const _centerY: float = ((_bounds.height / 2)) / this.z();

		return new Vector2(_centerX, _centerY);
	}

	protected onSpaceUp(_event: KeyboardEvent): void {
		_event.preventDefault();

		this.canPan.set(this._previousCanPanState);
	}

	protected onSpaceDown(_event: KeyboardEvent): void {
		if (_event.repeat)
			return;

		_event.preventDefault();

		this._previousCanPanState = this.canPan();

		if (!this._previousCanPanState)
			this.canPan.set(true);
	}

	protected onMouseMove(_event: MouseEvent): void {
		this.updateMousePosition(_event);

		if (this.isMouseDown() && this.canPan()) {
			this.x.set(this.canvasOffsetPoint().x + this.mouseX() - this.mouseDownPoint().x);
			this.y.set(this.canvasOffsetPoint().y + this.mouseY() - this.mouseDownPoint().y);

			this._fastUpdate(this.x(), this.y(), this.z());
		}
	}

	protected onMouseDown(_event: MouseEvent): void {
		if (checkBit(_event.buttons, EMouseButtons.LEFT) || checkBit(_event.buttons, EMouseButtons.AUX)) {
			this.isMouseDown.set(true);
			this.mouseDownPoint.set(new Vector2(this.mouseX(), this.mouseY()));
			this.canvasOffsetPoint.set(new Vector2(this.x(), this.y()));

			if (checkBit(_event.buttons, EMouseButtons.AUX)) {
				this._previousCanPanState = this.canPan();

				if (!this._previousCanPanState)
					this.canPan.set(true);
			}
		}
	}

	protected onMouseUp(_event: MouseEvent): void {
		this.isMouseDown.set(false);

		if (checkBit(_event.buttons, EMouseButtons.AUX))
			this.canPan.set(this._previousCanPanState);
	}

	protected onMouseWheel(_event: WheelEvent): void {
		_event.preventDefault();

		if (_event.ctrlKey) {
			const _dz: float = -_event.deltaY * this.zoomFactor();
			const _zPrime: float = this.z();
			const _zoom: float = Math.max(this.minZoom(), _zPrime + _dz);

			if (_zPrime === _zoom)
				return;

			this.isPanning.set(true);
			this.applyTransition();

			// Needs to be stored before z() is updated.
			const _worldX: float = this.mouseView().x;
			const _worldY: float = this.mouseView().y;

			this.z.set(_zoom);
			this.x.set(this.mouseX() - (_worldX * _zoom));
			this.y.set(this.mouseY() - (_worldY * _zoom));

			this._fastUpdate(this.x(), this.y(), this.z());
		} else
			this.isPanning.set(false);
	}

	protected updateMousePosition(_event: MouseEvent): void {
		const _bounds: DOMRect = this.elementRef.nativeElement.getBoundingClientRect();

		this.mouseX.set(_event.clientX - (_bounds.left + window.scrollX));
		this.mouseY.set(_event.clientY - (_bounds.top + window.scrollY));
	}

	public override ngOnDestroy(): void {
		this.viewboxService.targetSelector.set(null);
		this.resizeObserver.disconnect();
		super.ngOnDestroy();
	}

	// Needed to resolve parent signals not updating this directive signal in time.
	public repaint(_x: float, _y: float, _z: float): void {
		this._fastUpdate(_x, _y, _z);
	}

	public applyTransition(): void {
		const _transform: string = `all ${this.animationLength}s ease-in-out`;

		this.elementRef.nativeElement.style.setProperty('transition', _transform);
		this.contentWrapper()?.nativeElement.style.setProperty('transition', _transform);

		this._releaseTransition();
	}

	private _releaseTransition(): void {
		setTimeout(() => {
			this.elementRef.nativeElement.style.setProperty('transition', '');
			this.contentWrapper()?.nativeElement.style.setProperty('transition', '');
		}, this.animationLength * 1000);
	}

	private _fastUpdate(_x: float, _y: float, _z: float): void {
		const _wrapper: HTMLElement = this.contentWrapper()?.nativeElement;

		const _transform: string = `matrix(${_z}, 0, 0, ${_z}, ${_x}, ${_y})`;

		requestAnimationFrame(() => {
			_wrapper.style.setProperty('transform', _transform);
		});
	}

	private _handleResize(_entries: ResizeObserverEntry[]): void {
		this.waitingForResize.set(false);
	}

}
